import React from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Paper,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface Question {
  name: string;
  question: string;
  options: string[];
}

interface FormData {
  [key: string]: string;
}

// Workplace Readiness & Compliance Questions Data
const questionsData: Question[] = [
  {
    name: "question1",
    question: "1. What is the best way to show professionalism at work?",
    options: [
      "Wearing headphones during your shift",
      "Arriving on time and dressing appropriately",
      "Texting friends while working",
      "Leaving early without permission",
    ],
  },
  {
    name: "question2",
    question:
      "2. If you're going to be late to your worksite, what should you do?",
    options: [
      "Wait until the next day to explain",
      "Call or email your supervisor as soon as possible",
      "Tell your friend to cover for you",
      "Just don't show up",
    ],
  },
  {
    name: "question3",
    question:
      "3. What should you do if you feel unsafe or uncomfortable at your job site?",
    options: [
      "Quit immediately",
      "Keep it to yourself",
      "Tell another youth worker",
      "Report it to your site supervisor and DOES staff",
    ],
  },
  {
    name: "question4",
    question:
      "4. Which of the following actions could result in dismissal from the program?",
    options: [
      "Asking questions about your job",
      "Repeated unexcused absences",
      "Participating in team meetings",
      "Completing your work early",
    ],
  },
  {
    name: "question5",
    question: "5. Why is submitting your timesheet on time important?",
    options: [
      "So your friends get paid",
      "To avoid extra work",
      "To make sure you get paid correctly and on time",
      "It's optional",
    ],
  },
  {
    name: "question6",
    question: "6. What does being a 'team player' mean at work?",
    options: [
      "Competing with coworkers",
      "Letting others do your tasks",
      "Helping others and communicating respectfully",
      "Avoiding feedback",
    ],
  },
  {
    name: "question7",
    question:
      "7. What is the first thing you should do on your first day of work?",
    options: [
      "Take a selfie",
      "Introduce yourself to your supervisor",
      "Sit quietly until someone notices you",
      "Ask when lunch is",
    ],
  },
  {
    name: "question8",
    question: "8. What should you do if you don't know how to complete a task?",
    options: [
      "Guess and hope it's right",
      "Ignore it",
      "Complain about it",
      "Ask your supervisor for help or clarification",
    ],
  },
  {
    name: "question9",
    question: "9. What does it mean to be accountable at work?",
    options: [
      "Always watching what others are doing",
      "Letting others take credit for your work",
      "Taking responsibility for your actions and performance",
      "Blaming coworkers for mistakes",
    ],
  },
  {
    name: "question10",
    question:
      "10. How can you make a strong impression during your MBSYEP experience?",
    options: [
      "Doing the bare minimum",
      "Dressing casually and arriving late",
      "Being reliable, respectful, and eager to learn",
      "Talking only to other youth",
    ],
  },
];

// Create validation schema dynamically
const schemaShape = questionsData.reduce((acc, { name }) => {
  acc[name] = yup.string().required("This field is required");
  return acc;
}, {} as Record<string, yup.StringSchema>);

const schema = yup.object().shape(schemaShape);

// Create default values dynamically
const defaultValues = questionsData.reduce((acc, { name }) => {
  acc[name] = "";
  return acc;
}, {} as FormData);

function Test() {
  const { t } = useTranslation("JobApplication");

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: FormData) => {
    console.log("Certificate Assessment Results:", data);
    // Add your submission logic here
    // For example: API call to submit test results
  };

  const handleReAttempt = () => {
    reset(defaultValues);
  };

  return (
    <div className="flex flex-col p-10 sm:p-10 container">
      <div className="flex flex-col w-full max-w-6xl">
        <div className="sm:mt-5"></div>
        <Paper className="mt-5 sm:mt-5 p-24 pb-28 sm:p-5 sm:pb-10 rounded-2xl">
          <Typography
            variant="h4"
            className="mb-24 text-center font-semibold text-gray-800"
          >
            MBSYEP Certification Assessment
          </Typography>

          <Typography
            variant="body1"
            className="mb-32 text-center text-gray-600"
          >
            Please answer all questions to complete your workplace readiness and
            compliance assessment.
          </Typography>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="px-0 sm:px-24 space-y-24"
          >
            <div className="mb-24"></div>

            {questionsData.map(({ name, question, options }) => (
              <FormControl
                component="fieldset"
                key={name}
                error={!!errors[name]}
                fullWidth
                className="bg-gray-50 p-16 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors duration-200"
              >
                <FormLabel
                  component="legend"
                  className="font-semibold text-gray-800 mb-12"
                >
                  {question}
                </FormLabel>
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} className="ml-8">
                      {options.map((opt) => (
                        <FormControlLabel
                          key={opt}
                          value={opt}
                          control={
                            <Radio
                              sx={{
                                color: "#6b7280",
                                "&.Mui-checked": {
                                  color: "#3b82f6",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography className="text-gray-700">
                              {opt}
                            </Typography>
                          }
                          className="mb-8"
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
                {errors[name] && (
                  <Typography
                    color="error"
                    variant="caption"
                    className="mt-8 font-medium"
                  >
                    {errors[name]?.message}
                  </Typography>
                )}
              </FormControl>
            ))}
          </form>

          <div className="flex justify-between items-center mt-32 pt-24 border-t border-gray-200">
            <Typography variant="body2" className="text-gray-600 italic">
              All questions must be answered to submit the assessment
            </Typography>

            <div className="flex gap-12">
              <Button
                className="px-24 py-12 font-semibold"
                variant="outlined"
                color="secondary"
                onClick={handleReAttempt}
              >
                {t("ReAttampt_BTN") || "Re-Attempt"}
              </Button>
              <Button
                className="px-24 py-12 font-semibold"
                variant="contained"
                type="submit"
                color="primary"
                disabled={!isValid}
                onClick={handleSubmit(onSubmit)}
              >
                {t("Submit_BTN") || "Submit Assessment"}
              </Button>
            </div>
          </div>
        </Paper>
      </div>
    </div>
  );
}

export default Test;
